/**
 * Step 3 do wizard /imports — preview com tabela e edição inline (TH-53212).
 *
 * Carrega:
 *  - GET /api/imports/{batchId}            → summary (total, ok, error, warning)
 *  - GET /api/imports/{batchId}/lines      → linhas paginadas, com filtro por status
 *  - PATCH /api/imports/{batchId}/lines/{lineId} → re-valida raw editado
 *
 * Decisões:
 *  - Filtro por status mapeia FilterPills → query param `?status=ok|error|warning`.
 *    "Pendentes" = pending (linhas que ainda não foram validadas; raro chegar aqui).
 *  - Paginação server-side via offset/limit (50 por página, default do backend).
 *  - Colunas: linha, status, sku, descricao, preco_regular, preco_oferta, erros.
 *    Os 4 campos do produto vêm de `parsed` (validado) com fallback para `raw`
 *    (chave bruta do header da planilha) — isso cobre linhas inválidas onde o
 *    parse não rodou pra todo campo.
 *  - Edição inline: célula clicável → <Input> com Enter (salva) / Esc (cancela).
 *    PATCH manda `raw` inteiro com a chave editada substituída; backend re-roda
 *    `validate_row` e devolve a linha já com novo status/parsed/errors.
 *  - Sucesso de PATCH invalida ["import-lines", batchId] e ["import-batch", batchId]
 *    para refrescar contadores e linhas — também o filtro pode mudar (linha era
 *    error e virou ok).
 *  - "Próximo" só habilita se `error_rows === 0`. Step 4 chega no TH-53213.
 */

import { useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { Badge, type BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  DataTable,
  type DataTableColumn,
} from "@/components/ui/DataTable";
import { FilterPills } from "@/components/ui/FilterPills";
import { Input } from "@/components/ui/Input";

interface ImportLineDetail {
  id: string;
  row_number: number;
  status: string;
  raw: Record<string, unknown>;
  parsed: Record<string, unknown> | null;
  errors: string[] | null;
  warnings: string[] | null;
}

interface ImportLinesPage {
  items: ImportLineDetail[];
  total: number;
  offset: number;
  limit: number;
}

interface ImportPreview {
  id: string;
  status: string;
  store_id: string | null;
  mapping_id: string | null;
  file_url: string;
  total_rows: number;
  ok_rows: number;
  error_rows: number;
  warning_rows: number;
  created_at: string;
}

type FilterValue = "all" | "ok" | "warning" | "error" | "pending";

const PAGE_SIZE = 50;

const FILTER_OPTIONS: readonly { label: string; value: FilterValue }[] = [
  { label: "Todas", value: "all" },
  { label: "Válidas", value: "ok" },
  { label: "Avisos", value: "warning" },
  { label: "Inválidas", value: "error" },
  { label: "Pendentes", value: "pending" },
];

function statusToVariant(status: string): BadgeVariant {
  switch (status) {
    case "ok":
    case "applied":
      return "success";
    case "warning":
      return "warn";
    case "error":
      return "destructive";
    default:
      return "muted";
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case "ok":
      return "válida";
    case "warning":
      return "aviso";
    case "error":
      return "inválida";
    case "pending":
      return "pendente";
    case "applied":
      return "aplicada";
    default:
      return status;
  }
}

/** Lê `parsed[key]` com fallback para `raw[key]`. Retorna string vazia se nada. */
function readField(line: ImportLineDetail, key: string): string {
  const fromParsed = line.parsed?.[key];
  if (fromParsed !== undefined && fromParsed !== null && fromParsed !== "") {
    return String(fromParsed);
  }
  // raw pode usar a mesma chave (mapping já normalizou) ou o header original;
  // a UI do Step 3 prioriza a chave normalizada — se o operador mapeou
  // "Cód." → sku, o backend salva `raw["Cód."]` mas `parsed["sku"]`.
  const fromRaw = line.raw?.[key];
  return fromRaw !== undefined && fromRaw !== null ? String(fromRaw) : "";
}

async function fetchPreview(batchId: string): Promise<ImportPreview> {
  const res = await fetch(`/api/imports/${batchId}`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as ImportPreview;
}

async function fetchLines(
  batchId: string,
  filter: FilterValue,
  page: number,
): Promise<ImportLinesPage> {
  const params = new URLSearchParams({
    offset: String((page - 1) * PAGE_SIZE),
    limit: String(PAGE_SIZE),
  });
  if (filter !== "all") {
    params.set("status", filter);
  }
  const res = await fetch(
    `/api/imports/${batchId}/lines?${params.toString()}`,
    { credentials: "include" },
  );
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as ImportLinesPage;
}

interface PatchVars {
  lineId: string;
  raw: Record<string, unknown>;
}

async function patchLine(
  batchId: string,
  vars: PatchVars,
): Promise<ImportLineDetail> {
  const res = await fetch(
    `/api/imports/${batchId}/lines/${vars.lineId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ raw: vars.raw }),
    },
  );
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch {
      // não-json — mantém HTTP <status>
    }
    throw new Error(detail);
  }
  return (await res.json()) as ImportLineDetail;
}

interface EditableCellProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  /** Em linhas válidas (ok/applied) o admin não precisa editar; só mostra texto. */
  editable: boolean;
  /** Tornar a célula mono (preços/IDs). */
  mono?: boolean;
}

function EditableCell({
  value,
  onSave,
  editable,
  mono,
}: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!editable) {
    return (
      <span
        className={
          mono ? "font-mono text-[11px] tabular-nums" : undefined
        }
      >
        {value || (
          <span className="text-[var(--color-muted-foreground)]">—</span>
        )}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value);
          setError(null);
          setEditing(true);
        }}
        className={[
          "w-full rounded px-1 py-0.5 text-left transition-colors",
          "hover:bg-[var(--color-secondary)] focus-visible:bg-[var(--color-secondary)]",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-ring)]",
          mono ? "font-mono text-[11px] tabular-nums" : "",
        ].join(" ")}
        aria-label={`editar ${value || "(vazio)"}`}
      >
        {value || (
          <span className="text-[var(--color-muted-foreground)]">
            (vazio — clique para editar)
          </span>
        )}
      </button>
    );
  }

  async function commit() {
    if (draft === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "falha ao salvar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <Input
        autoFocus
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setEditing(false);
            setError(null);
          }
        }}
        onBlur={() => {
          // Só salva no blur se mudou — evita PATCH desnecessário.
          if (!saving && draft !== value) {
            void commit();
          } else if (!saving) {
            setEditing(false);
          }
        }}
        className={mono ? "font-mono text-[11px] tabular-nums" : "h-8"}
      />
      {error ? (
        <span
          role="alert"
          className="text-[10px] text-[var(--color-destructive)]"
        >
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface ImportsStep3PreviewProps {
  batchId: string;
  onBack: () => void;
  onNext: () => void;
}

export function ImportsStep3Preview({
  batchId,
  onBack,
  onNext,
}: ImportsStep3PreviewProps) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FilterValue>("all");
  const [page, setPage] = useState(1);

  const previewQuery = useQuery({
    queryKey: ["import-batch", batchId],
    queryFn: () => fetchPreview(batchId),
  });

  const linesQuery = useQuery({
    queryKey: ["import-lines", batchId, filter, page],
    queryFn: () => fetchLines(batchId, filter, page),
    // Preview pode estar em "uploaded" no primeiro GET — esperamos preview
    // antes de buscar linhas.
    enabled: previewQuery.data?.status === "preview",
  });

  const patchMutation = useMutation({
    mutationFn: (vars: PatchVars) => patchLine(batchId, vars),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["import-lines", batchId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["import-batch", batchId],
      });
    },
  });

  function handleSaveCell(
    line: ImportLineDetail,
    columnKey: string,
    newValue: string,
  ): Promise<void> {
    // O backend espera o `raw` completo. Decidimos qual chave bruta atualizar:
    //  - se a chave da coluna existe em `line.raw` direto → atualiza ela;
    //  - senão, adiciona a chave com nome "normalizado" (sku/descricao/...).
    //    O backend reaproveita o mapping; se `mapping_id` não estiver setado,
    //    `suggest_mapping` corre sobre as chaves enviadas — manter as chaves
    //    originais é mais seguro.
    const newRaw: Record<string, unknown> = { ...line.raw };
    if (Object.prototype.hasOwnProperty.call(newRaw, columnKey)) {
      newRaw[columnKey] = newValue;
    } else {
      // Procura uma chave "parecida" no raw (case-insensitive, sem acento).
      const target = Object.keys(newRaw).find(
        (k) => normalize(k) === normalize(columnKey),
      );
      if (target) {
        newRaw[target] = newValue;
      } else {
        newRaw[columnKey] = newValue;
      }
    }
    return patchMutation
      .mutateAsync({ lineId: line.id, raw: newRaw })
      .then(() => undefined);
  }

  const columns = useMemo<DataTableColumn<ImportLineDetail>[]>(
    () => [
      {
        key: "row_number",
        header: "Linha",
        mono: true,
        width: "70px",
        cell: (r) => r.row_number,
      },
      {
        key: "status",
        header: "Status",
        width: "110px",
        cell: (r) => (
          <Badge variant={statusToVariant(r.status)}>
            {statusLabel(r.status)}
          </Badge>
        ),
      },
      {
        key: "sku",
        header: "SKU",
        width: "140px",
        cell: (r) => (
          <EditableCell
            value={readField(r, "sku")}
            onSave={(v) => handleSaveCell(r, "sku", v)}
            editable={r.status !== "applied"}
            mono
          />
        ),
      },
      {
        key: "descricao",
        header: "Descrição",
        cell: (r) => (
          <EditableCell
            value={readField(r, "descricao")}
            onSave={(v) => handleSaveCell(r, "descricao", v)}
            editable={r.status !== "applied"}
          />
        ),
      },
      {
        key: "preco_regular",
        header: "Preço regular",
        align: "right",
        width: "130px",
        cell: (r) => (
          <EditableCell
            value={readField(r, "preco_regular")}
            onSave={(v) => handleSaveCell(r, "preco_regular", v)}
            editable={r.status !== "applied"}
            mono
          />
        ),
      },
      {
        key: "preco_oferta",
        header: "Preço oferta",
        align: "right",
        width: "130px",
        cell: (r) => (
          <EditableCell
            value={readField(r, "preco_oferta")}
            onSave={(v) => handleSaveCell(r, "preco_oferta", v)}
            editable={r.status !== "applied"}
            mono
          />
        ),
      },
      {
        key: "errors",
        header: "Mensagens",
        cell: (r) => {
          const items = [...(r.errors ?? []), ...(r.warnings ?? [])];
          if (items.length === 0) {
            return (
              <span className="text-[var(--color-muted-foreground)]">—</span>
            );
          }
          return (
            <ul className="flex flex-col gap-0.5 text-[11px]">
              {items.map((msg, i) => (
                <li
                  key={i}
                  className={
                    (r.errors ?? []).includes(msg)
                      ? "text-[var(--color-destructive)]"
                      : "text-[var(--color-warning)]"
                  }
                >
                  {msg}
                </li>
              ))}
            </ul>
          );
        },
      },
    ],
    // handleSaveCell é estável o suficiente para o uso aqui (depende apenas do
    // batchId via closure de patchMutation, que é estável dentro do componente).

    [],
  );

  const summary = previewQuery.data;
  const lines = linesQuery.data?.items ?? [];
  const total = linesQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canProceed = (summary?.error_rows ?? 0) === 0 && summary?.status === "preview";

  return (
    <div className="flex flex-col gap-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
        Passo 3 de 4 · Conferir e corrigir
      </div>

      {previewQuery.isLoading ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          carregando preview…
        </p>
      ) : previewQuery.error ? (
        <div
          role="alert"
          className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 p-3 text-xs text-[var(--color-destructive)]"
        >
          erro ao carregar preview do batch
        </div>
      ) : summary ? (
        <div className="grid grid-cols-4 gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-4 text-xs">
          <SummaryCell label="Total" value={summary.total_rows} />
          <SummaryCell
            label="Válidas"
            value={summary.ok_rows}
            tone="success"
          />
          <SummaryCell
            label="Avisos"
            value={summary.warning_rows}
            tone="warn"
          />
          <SummaryCell
            label="Inválidas"
            value={summary.error_rows}
            tone="destructive"
          />
        </div>
      ) : null}

      {summary?.status === "preview" ? (
        <>
          <FilterPills
            options={FILTER_OPTIONS}
            value={filter}
            onChange={(v) => {
              setFilter(v);
              setPage(1);
            }}
          />

          {linesQuery.isLoading ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              carregando linhas…
            </p>
          ) : linesQuery.error ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 p-3 text-xs text-[var(--color-destructive)]"
            >
              erro ao carregar linhas do batch
            </div>
          ) : (
            <DataTable
              columns={columns}
              rows={lines}
              rowKey={(r) => r.id}
              emptyMessage="Nenhuma linha para o filtro selecionado."
            />
          )}

          {total > PAGE_SIZE ? (
            <div className="flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
              <span>
                Página {page} de {totalPages} · {total} linha
                {total === 1 ? "" : "s"}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((p) => Math.min(totalPages, p + 1))
                  }
                >
                  Próxima
                </Button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {summary && summary.error_rows > 0 ? (
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Existem {summary.error_rows} linha
          {summary.error_rows === 1 ? "" : "s"} inválida
          {summary.error_rows === 1 ? "" : "s"} — corrija antes de
          continuar.
        </p>
      ) : null}

      <div className="flex justify-between gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onBack}>
          ← Voltar
        </Button>
        <Button type="button" onClick={onNext} disabled={!canProceed}>
          Próximo →
        </Button>
      </div>
    </div>
  );
}

interface SummaryCellProps {
  label: string;
  value: number;
  tone?: "success" | "warn" | "destructive";
}

function SummaryCell({ label, value, tone }: SummaryCellProps) {
  const toneClass =
    tone === "success"
      ? "text-[var(--color-success)]"
      : tone === "warn"
        ? "text-[var(--color-warning)]"
        : tone === "destructive"
          ? "text-[var(--color-destructive)]"
          : "text-[var(--color-foreground)]";
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <span
        className={`mt-1 font-mono text-base font-semibold tabular-nums ${toneClass}`}
      >
        {value}
      </span>
    </div>
  );
}

function normalize(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
}
